import { z } from "zod";
import { reportTemplateTextSchema } from "./reportTextTemplate";

/**
 * Generic report-canvas serialization: the persisted form of a canvas layout,
 * shared by every consumer of the canvas framework.
 *
 * Every surface lays its components on one grid (the Report grid): 12 columns
 * wide, rows of a fixed unit height. Each item has a place and a size in grid
 * cells, `{ x, y, w, h }` (x, y from the top-left, zero-based), and carries
 * one component config. Position is the identity of nothing: runtime
 * drag-and-drop ids are regenerated on load, and the canvas compacts items
 * upward (gravity) and resolves overlaps when it reads a layout.
 *
 * Layouts saved before the grid are rows of components (`data[row][cell]`,
 * each with an optional column `span`); they still parse, and the canvas
 * converts them to grid items on read (row by row, each row as tall as its
 * tallest component's default height) — no stored layout is migrated.
 *
 * The component schema is open (`passthrough`) so surfaces can register
 * their own widget types without touching shared code.
 */

/** The columns of the Report grid. */
export const REPORT_CANVAS_COLUMNS = 12;
/**
 * The most components a layout holds, shared by the serialized schema and the
 * canvas UI so adds that would exceed it are refused at interaction time
 * instead of failing at save time.
 */
export const REPORT_CANVAS_MAX_COMPONENTS = 72;
/** The tallest a component may be, in grid rows. */
export const REPORT_CANVAS_MAX_ITEM_HEIGHT = 48;
/** The deepest grid row a component may start on. */
export const REPORT_CANVAS_MAX_GRID_ROW = REPORT_CANVAS_MAX_COMPONENTS * 48;

/** Caps on a layout saved before the grid (rows of components). */
const LEGACY_MAX_ROWS = 12;
const LEGACY_MAX_CELLS_PER_ROW = 6;

/** A calendar day, YYYY-MM-DD. */
const ReportDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");

/**
 * A date range a component pins its data to, in place of the page's: the
 * last N days, or two calendar days inclusive. The source's catalog decides
 * how long a range it answers.
 */
export const ReportDateRangeSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("last_days"),
    days: z.int().min(1).max(3660),
  }),
  z.strictObject({
    kind: z.literal("between"),
    from: ReportDaySchema,
    to: ReportDaySchema,
  }),
]);
export type ReportDateRange = z.infer<typeof ReportDateRangeSchema>;

/**
 * Where a component's data comes from, when the surface fetches it per
 * component: a query in a catalog the surface knows (`catalog` names it, and
 * the catalog validates `query`), optionally pinned to its own date range.
 * The canvas never reads `query`; the surface runs it and publishes the
 * answer as a dataset (see {@link componentDatasetKey}).
 */
export const ReportDataSourceSchema = z.strictObject({
  catalog: z.string().min(1).max(64),
  query: z.unknown(),
  /** Absent: the page's range. */
  range: ReportDateRangeSchema.optional(),
});
export type ReportDataSource = z.infer<typeof ReportDataSourceSchema>;

export const REPORT_NOTE_CONDITIONS = [
  "positive",
  "zero",
  "present",
  "absent",
] as const;

/**
 * A line of text above or below a component's body, optionally shown only
 * when a value is positive, zero, present (not null) or absent.
 */
export const ReportComponentNoteSchema = z.strictObject({
  text: reportTemplateTextSchema(1000),
  when: z
    .strictObject({
      value: reportTemplateTextSchema(200),
      is: z.enum(REPORT_NOTE_CONDITIONS),
    })
    .optional(),
  placement: z.enum(["above", "below"]).optional(),
});
export type ReportComponentNote = z.infer<typeof ReportComponentNoteSchema>;

/** A path in the app, e.g. "/tasks?status=open". */
export const ReportAppPathSchema = z
  .string()
  .max(500)
  .regex(/^\/(?![/\\])/, "Use a path in the app, starting with one /");

/**
 * Identity + plumbing fields every canvas component config shares. Concrete
 * chart schemas (see `./queryReport.ts`) extend this so a component validated
 * by either schema round-trips through the other.
 */
export const CanvasComponentBaseSchema = z.object({
  id: z.string().min(1),
  /**
   * Named dataset this component reads from the surface's data context.
   * Undefined = the surface's default dataset (single-dataset surfaces like
   * the query builder never set this).
   */
  datasetKey: z.string().optional(),
  /**
   * Config schema version for this component type. Absent = 1. Component
   * config migrations run client-side against the canvas registry on read.
   */
  v: z.number().int().min(1).optional(),
  /**
   * Grid columns the component spanned on its row, in a layout saved before
   * the grid (rows of components). Read only by the conversion to grid items.
   */
  span: z.number().int().min(1).max(REPORT_CANVAS_COLUMNS).optional(),
  /**
   * Shown above the component. Absent or blank = its type's default title,
   * if it has one. May hold `{placeholders}` of its dataset's values.
   */
  title: z.string().max(200).optional(),
  /** Shown under the title; may hold `{placeholders}`. */
  description: reportTemplateTextSchema(2000).optional(),
  /** Help shown from an info icon beside the title; may hold `{placeholders}`. */
  tooltip: reportTemplateTextSchema(1000).optional(),
  /** Lines of text above or below the body. */
  notes: z.array(ReportComponentNoteSchema).max(10).optional(),
  /** A link at the foot of the component to a related page. */
  link: z
    .strictObject({
      label: z.string().min(1).max(100),
      href: ReportAppPathSchema,
    })
    .optional(),
  /** Shown in place of the body when the dataset has no rows. */
  emptyText: z.string().max(200).optional(),
  /**
   * The component's own data: the surface runs it and publishes the answer
   * as the dataset `datasetKey` (absent = the component's id).
   */
  source: ReportDataSourceSchema.optional(),
  /**
   * A second source, of one row of totals, that the component's
   * `{placeholders}` read beside its own dataset's (a chart's median above
   * its buckets). Pinned to `source.range` unless it sets its own.
   */
  textSource: ReportDataSourceSchema.optional(),
});

/**
 * The dataset a component reads: the one its `datasetKey` names; for a
 * component with its own `source`, its id when it names none. Undefined =
 * the surface's default dataset.
 */
export function componentDatasetKey(component: {
  id: string;
  datasetKey?: string;
  source?: unknown;
}): string | undefined {
  return component.datasetKey ?? (component.source ? component.id : undefined);
}

/** The dataset a component's `textSource` is published as; undefined without one. */
export function componentTextDatasetKey(component: {
  id: string;
  datasetKey?: string;
  textSource?: unknown;
}): string | undefined {
  if (!component.textSource) return undefined;
  return `${component.datasetKey ?? component.id}#text`;
}

/** Open component config: widget-specific fields flow through untyped by design. */
export const CanvasComponentConfigSchema = CanvasComponentBaseSchema.extend({
  type: z.string().min(1),
}).passthrough();
export type CanvasComponentConfig = z.infer<typeof CanvasComponentConfigSchema>;

/** One component on the Report grid: its place and size in grid cells. */
export const ReportCanvasItemSchema = z
  .object({
    x: z
      .number()
      .int()
      .min(0)
      .max(REPORT_CANVAS_COLUMNS - 1),
    y: z.number().int().min(0).max(REPORT_CANVAS_MAX_GRID_ROW),
    w: z.number().int().min(1).max(REPORT_CANVAS_COLUMNS),
    h: z.number().int().min(1).max(REPORT_CANVAS_MAX_ITEM_HEIGHT),
    component: CanvasComponentConfigSchema,
  })
  .refine(
    (item) => item.x + item.w <= REPORT_CANVAS_COLUMNS,
    `A component fits within the grid's ${REPORT_CANVAS_COLUMNS} columns.`,
  );
export type ReportCanvasItem = z.infer<typeof ReportCanvasItemSchema>;

/**
 * The versioned template a layout started from, on a surface whose default is
 * a template (the AR cash dashboard). Lets the surface tell a layout built on
 * an older template version from one that is current.
 */
export const ReportCanvasTemplateStampSchema = z.object({
  id: z.string().min(1).max(64),
  version: z.number().int().min(1),
});
export type ReportCanvasTemplateStamp = z.infer<
  typeof ReportCanvasTemplateStampSchema
>;

/** A layout on the Report grid: its items, capped. */
export const ReportCanvasGridLayoutSchema = z.object({
  items: z.array(ReportCanvasItemSchema).max(REPORT_CANVAS_MAX_COMPONENTS),
  template: ReportCanvasTemplateStampSchema.optional(),
});
export type ReportCanvasGridLayout = z.infer<
  typeof ReportCanvasGridLayoutSchema
>;

/** A layout saved before the grid: rows of component configs, capped. */
export const ReportCanvasRowsLayoutSchema = z.object({
  data: z
    .array(
      z
        .array(CanvasComponentConfigSchema)
        .min(1)
        .max(LEGACY_MAX_CELLS_PER_ROW)
        .refine(
          (row) =>
            row.reduce((total, cell) => total + (cell.span ?? 1), 0) <=
            REPORT_CANVAS_COLUMNS,
          `A row spans at most ${REPORT_CANVAS_COLUMNS} columns.`,
        ),
    )
    .max(LEGACY_MAX_ROWS),
});
export type ReportCanvasRowsLayout = z.infer<
  typeof ReportCanvasRowsLayoutSchema
>;

/**
 * A serialized canvas layout: grid items, or rows saved before the grid (the
 * canvas converts those on read; it always saves grid items).
 */
export const ReportCanvasLayoutSchema = z.union([
  ReportCanvasGridLayoutSchema,
  ReportCanvasRowsLayoutSchema,
]);
export type ReportCanvasLayout = z.infer<typeof ReportCanvasLayoutSchema>;

/** An empty layout on the grid. */
export const EMPTY_REPORT_CANVAS_LAYOUT: ReportCanvasGridLayout = { items: [] };

/** A layout's component configs in reading order, whichever shape it was saved in. */
export function reportCanvasComponents(
  layout: ReportCanvasLayout,
): CanvasComponentConfig[] {
  if ("items" in layout)
    return [...layout.items]
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .map((item) => item.component);
  return layout.data.flat();
}

/**
 * The stored JSONB envelope for a surface layout row in `core_saved_reports`
 * (a per-user override or org default keyed by `surface_id`). Same envelope
 * shape as the query-builder's saved-report document (`./queryReport.ts`)
 * minus `query` — surface layouts chart page-provided datasets, not a query.
 */
export const CanvasSavedReportDocumentSchema = z.object({
  schemaVersion: z.number().int(),
  report: ReportCanvasLayoutSchema,
});
/** 2: the layout moved from rows of components to the Report grid. */
export const CURRENT_CANVAS_SAVED_REPORT_SCHEMA_VERSION = 2;
export type CanvasSavedReportDocument = z.infer<
  typeof CanvasSavedReportDocumentSchema
>;
